import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useMode } from "../contexts/ModeContext";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import PremiumGateModal from "./PremiumGateModal";

/**
 * Analyst Mode access is gated by:
 *   1. Admin / analyst role  → unconditional access
 *   2. PREMIUM or ENTERPRISE active subscription → access included
 *
 * Standard-tier subscribers and free authenticated users see a soft upsell
 * modal pointing them at the Premium plan. Anonymous visitors still go to
 * /signin first (they can subscribe afterwards).
 */
const ANALYST_TIERS = new Set(["PREMIUM", "ENTERPRISE"]);

export default function ModeToggle() {
  const { mode, setMode } = useMode();
  const { user } = useAuth();
  const { tier, active } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [gateOpen, setGateOpen] = useState(false);

  const isPrivilegedRole = user?.role === "admin" || user?.role === "analyst";
  const hasAnalystSub = active && ANALYST_TIERS.has(tier);
  const canUseAnalyst = isPrivilegedRole || hasAnalystSub;

  const switchTo = (next) => {
    if (next === mode) return;
    if (next === "analyst") {
      if (!user) {
        navigate("/signin?msg=analyst-required");
        return;
      }
      if (!canUseAnalyst) {
        setGateOpen(true);
        return;
      }
    }
    setMode(next);
    if (next === "analyst" && !location.pathname.startsWith("/analyst")) {
      navigate("/analyst/dashboard");
    } else if (next === "standard" && location.pathname.startsWith("/analyst")) {
      navigate("/");
    }
  };

  const analystLocked = !!user && !canUseAnalyst;

  return (
    <>
      <div
        role="tablist"
        aria-label="Mode toggle"
        data-testid="mode-toggle"
        className="relative inline-flex items-center bg-white border border-brand-border rounded-full p-1 shadow-sm"
      >
        {/* Sliding pill */}
        <motion.div
          layout
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className="absolute top-1 bottom-1 rounded-full bg-brand"
          style={{
            width: "calc(50% - 4px)",
            left: mode === "standard" ? 4 : "calc(50% + 0px)",
          }}
        />
        <button
          type="button"
          role="tab"
          aria-selected={mode === "standard"}
          onClick={() => switchTo("standard")}
          data-testid="mode-standard"
          className={`relative z-10 px-4 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
            mode === "standard" ? "text-white" : "text-brand"
          }`}
        >
          Standard
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "analyst"}
          onClick={() => switchTo("analyst")}
          data-testid="mode-analyst"
          data-locked={analystLocked}
          className={`relative z-10 px-4 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
            mode === "analyst" ? "text-white" : "text-brand"
          } ${!user ? "opacity-60" : analystLocked ? "opacity-80" : ""}`}
          title={
            !user
              ? "Sign in to access Analyst mode"
              : analystLocked
              ? "Premium plan required — click to learn more"
              : ""
          }
        >
          Analyst
          {analystLocked && (
            <span
              aria-hidden
              className="ml-1.5 inline-block align-middle h-1.5 w-1.5 rounded-full bg-[#E55B13]"
            />
          )}
        </button>
      </div>

      <PremiumGateModal open={gateOpen} onClose={() => setGateOpen(false)} />
    </>
  );
}
